import React, { useEffect, useState } from 'react';
import { usePatientById } from '@hooks/usePatientById';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';

import { DateRangeSelector } from '../../../../components/DateRangeSelector';
import PatientPicker from '../../../../components/Patient/PatientPicker';
import type { Patient } from '../../../../types/types';
import { STATUS_OPTIONS_BY_TAB } from '../../constants/statusOptions';

import type { TabType } from './types';

interface BillingFiltersProps {
  tab: TabType;
  patient?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  onFilterChange: (filters: {
    patient?: string;
    from?: string;
    to?: string;
    status?: string;
  }) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

// Use status options from the constants module
// The STATUS_OPTIONS_BY_TAB is imported above

export const BillingFilters: React.FC<BillingFiltersProps> = ({
  tab,
  patient,
  dateFrom,
  dateTo,
  status,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>(status || '');
  const [expandedMobile, setExpandedMobile] = useState(false);

  // Fetch patient data when we have a patient ID from URL
  const { patient: loadedPatient, loading: loadingPatient } = usePatientById(patient);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Update local state when URL params change
  useEffect(() => {
    setSelectedStatus(status || '');
  }, [status]);

  const handlePatientSelect = (patient: Patient | undefined) => {
    onFilterChange({
      patient: patient?.patient_id,
      from: dateFrom,
      to: dateTo,
      status,
    });
  };

  const handleDateRangeChange = (range: { start: Date | null; end: Date | null }) => {
    // Use local date formatting to avoid timezone issues
    const formatLocalDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const from = range.start ? formatLocalDate(range.start) : undefined;
    const to = range.end ? formatLocalDate(range.end) : undefined;

    onFilterChange({
      patient,
      from,
      to,
      status,
    });
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedStatus(value);
    onFilterChange({
      patient,
      from: dateFrom,
      to: dateTo,
      status: value || undefined,
    });
  };

  const handleClearStatus = () => {
    setSelectedStatus('');
    onFilterChange({
      patient,
      from: dateFrom,
      to: dateTo,
      status: undefined,
    });
  };

  const handleClearFilters = () => {
    setSelectedStatus('');
    onClearFilters();
  };

  const statusOptions = STATUS_OPTIONS_BY_TAB[tab] || [];

  // Calculate active filter count for mobile badge
  const activeFilterCount = [
    patient ? 1 : 0,
    selectedStatus ? 1 : 0,
    dateFrom || dateTo ? 1 : 0,
  ].reduce((sum, val) => sum + val, 0);

  // Generate filter summary text for mobile
  const getFilterSummary = () => {
    const parts = [];
    if (patient && loadedPatient) {
      const name =
        `${loadedPatient.first_name || ''} ${loadedPatient.last_name || ''}`.trim() || 'Patient';
      parts.push(name);
    } else if (patient) {
      parts.push('Patient');
    }
    if (selectedStatus) {
      const option = statusOptions.find(opt => opt.value === selectedStatus);
      parts.push(option?.label || 'Status');
    }
    if (dateFrom || dateTo) {
      parts.push('Date range');
    }
    return parts.length > 0 ? parts.join(', ') : 'No filters';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: isMobile && !expandedMobile ? 0.75 : isMobile ? 1.5 : 2,
        mb: 2,
        borderRadius: 1,
        bgcolor: '#fafafa',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={isMobile && !expandedMobile ? 0 : 2}>
        {/* Mobile collapsed header */}
        {isMobile ? (
          <Box>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setExpandedMobile(!expandedMobile);
              }}
              startIcon={
                <Badge badgeContent={activeFilterCount} color="primary">
                  <FilterListIcon />
                </Badge>
              }
              endIcon={expandedMobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                justifyContent: 'space-between',
                px: 1,
                py: 0.5,
                color: 'text.primary',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {getFilterSummary()}
                  </Typography>
                )}
              </Box>
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Box sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.secondary' }}>
              Filters
            </Box>
            {hasActiveFilters && (
              <Chip
                label="Active"
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            )}
          </Box>
        )}

        {/* Collapsible filter content */}
        <Collapse in={!isMobile || expandedMobile}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
              pt: isMobile ? 1 : 0,
            }}
          >
            {/* Patient Picker - First */}
            <Box sx={{ minWidth: 200 }}>
              <PatientPicker
                key={patient || 'no-patient'}
                onPatientSelect={handlePatientSelect}
                initialPatient={loadingPatient ? undefined : loadedPatient || undefined}
                size="small"
                disableAddNew={true}
              />
            </Box>

            {/* Status Single-Select - Second */}
            <FormControl size="small" fullWidth>
              <InputLabel shrink={!!selectedStatus}>Status</InputLabel>
              <Select
                value={selectedStatus}
                onChange={handleStatusChange}
                input={
                  <OutlinedInput
                    label={selectedStatus ? 'Status' : ''}
                    notched={!!selectedStatus}
                    endAdornment={
                      selectedStatus && (
                        <InputAdornment position="end" sx={{ mr: 3 }}>
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation();
                              handleClearStatus();
                            }}
                            onMouseDown={e => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  />
                }
                displayEmpty
                renderValue={value => {
                  if (!value) {
                    return '';
                  }
                  const option = statusOptions.find(opt => opt.value === value);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip
                        label={option?.label || value}
                        size="small"
                        color={option?.color || 'default'}
                        sx={{ height: 22 }}
                      />
                    </Box>
                  );
                }}
              >
                <MenuItem value="">
                  <Typography color="text.secondary">All</Typography>
                </MenuItem>
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range Selector - Third */}
            <DateRangeSelector
              value={(() => {
                // Parse the date strings as local dates
                const parseLocalDate = (dateStr: string) => {
                  const [year, month, day] = dateStr.split('-').map(Number);
                  const date = new Date(year, month - 1, day);
                  return date;
                };

                return {
                  start: dateFrom ? parseLocalDate(dateFrom) : null,
                  end: dateTo ? parseLocalDate(dateTo) : null,
                };
              })()}
              onChange={handleDateRangeChange}
              size="small"
              fullWidth
            />
          </Box>
        </Collapse>

        {/* Clear Filters Button */}
        {hasActiveFilters && (!isMobile || expandedMobile) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.secondary',
                  bgcolor: 'transparent',
                },
              }}
            >
              Clear All Filters
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
